<script setup lang="ts">
import { ref } from 'vue'
import Header from '@/layouts/partials/Header.vue'
import ImpersonationBanner from '@/components/ImpersonationBanner.vue'
import Sidebar from '@/layouts/partials/Sidebar.vue'
import Footer from '@/layouts/partials/Footer.vue'
import Notification from '@/layouts/partials/Notification.vue'
import SyncActivityPane from '@/layouts/partials/SyncActivityPane.vue'
import DrawerHost from '@/components/DrawerHost.vue'
import { useMainBoundsTracker } from '@/lib/utils/useMainBoundsTracker'
import { useUIStore } from '@/stores/uiStore'

/**
 * Default authenticated layout.
 *
 *   <Header />
 *   <body row>
 *     <Sidebar />
 *     <main grid>                             ← CSS Grid; columns animate
 *       <div .content>                         ← grid item, shrinks via grid
 *         <RouterView />
 *       <DrawerHost />                         ← absolute overlay
 *   <Footer />
 *
 * Push-content via CSS Grid. `<main>` is `grid` with animating
 * `grid-template-columns`. The push seam is `xl` (1280px): phone +
 * tablet + iPad (`<xl`) never push — the drawer is a wide opaque
 * absolute overlay floating over un-shrunk content; only desktop
 * pushes. `xl` (not `lg`) because iPad Pro 12.9" portrait is exactly
 * 1024px (`lg`); a `lg` seam would cram its editor into the 1/3
 * column. At `xl` every portrait iPad (≤1024) and most landscape
 * (≤1194) gets the roomy overlay instead:
 *
 *   phone + tablet (no xl): `grid-cols-1`                 (drawer is overlay)
 *   xl+ closed:             `xl:grid-cols-[1fr_0fr]`       (content full, drawer cell 0)
 *   xl+ open:               `xl:grid-cols-[2fr_1fr]`       (content 2/3, drawer cell 1/3)
 *
 * The grid-template-columns transition is browser-native (Chrome
 * 117+, Firefox 117+, Safari 16+) and smoothly animates the content
 * column's width from full → 2/3 over `duration-slower ease-panel`.
 * Same timing tokens as `<DrawerHost />`'s `<Transition>` slide so
 * push + slide land together.
 *
 * `<DrawerHost />` itself is `position: absolute` (escapes grid
 * flow) so it can fill phone width (full), float as a 3/4-width
 * card on tablet / iPad (`md:w-3/4`), or anchor to main's right edge
 * with `xl:w-1/3` once the desktop push kicks in. Its animation is
 * independent — slide via Vue `<Transition>` + translateX. main's
 * `overflow: hidden` clips the off-screen card.
 *
 * No custom theme extensions for layout proportions — native
 * Tailwind grid utilities express "drawer takes 1/3 of main" via
 * `grid-cols-[2fr_1fr]` directly.
 */
const ui = useUIStore()

// Publish `<main>`'s bounding rect as `--app-main-*` CSS custom
// properties on `:root` so global CSS (PrimeVue Toast anchor in
// `main.scss`) can position the body-teleported overlay relative
// to the main content region. PrimeVue v4's `<Toast>` hardcodes
// `appendTo: 'body'` internally — no public API to bound it.
// Tracking + CSS-var-driven overrides is our route around that.
const mainRef = ref<HTMLElement | null>(null)
useMainBoundsTracker(mainRef)
</script>

<template>
  <!--
    Linear-style elevation hierarchy: chrome (header / sidebar /
    footer) sits at the canvas tone (darker in dark mode), main
    content is the slightly elevated panel (lighter — surface tier).
    `bg-canvas` on the outer wrapper makes the chrome inherit it; the
    explicit `bg-surface` on `<main>` lifts the content area.

    Responsive structure:
      - `<md` (mobile): sidebar = fixed overlay drawer, hamburger in
        header opens it. Scrim sits behind drawer + dismisses on tap.
      - `md+`  (desktop): sidebar = in-flow flex child with width
        toggle (icons-only ↔ full). Chevron decorator straddling the
        sidebar/main divider drives the toggle.
  -->
  <div class="h-full flex flex-col bg-canvas">
    <!-- Above the header on purpose: an impersonated session carries the
         target's full write access, so whose account you are in must be the
         first thing on the page, on every route. -->
    <ImpersonationBanner />
    <Header />
    <div class="flex-1 flex min-h-0 relative">
      <!--
        `peer` is the load-bearing class that lets the chevron
        decorator (sibling, declared LATER in DOM order) react to
        sidebar hover via Tailwind's `peer-hover:` modifier. Without
        it the decorator would need a Vue ref + mouseenter/leave
        listeners; with it, pure CSS handles the visibility flip.
      -->
      <Sidebar class="peer" />

      <!--
        Mobile-only scrim — sits behind the open sidebar drawer +
        in front of main content. Tap-anywhere-outside dismisses.
        `absolute inset-0` bounds it to the layout's middle row (the
        positioned ancestor) so it dims the main content region
        without overlaying header / footer — the drawer + scrim are
        scoped to the SAME visual region. z-30 (below sidebar's
        z-40, above content). md:hidden keeps it out of the desktop
        layer cake entirely.
      -->
      <div
        v-if="ui.sidebarMobileOpen"
        class="absolute inset-0 z-30 bg-black/40 md:hidden"
        aria-hidden="true"
        @click="ui.closeSidebarMobile()"
      />

      <main
        ref="mainRef"
        class="flex-1 min-w-0 min-h-0 overflow-hidden relative grid grid-cols-1 bg-surface transition-[grid-template-columns] duration-slower ease-panel"
        :class="ui.drawer.right.isOpen ? 'xl:grid-cols-[2fr_1fr]' : 'xl:grid-cols-[1fr_0fr]'"
      >
        <div
          class="overflow-auto min-w-0 p-4 md:p-6 flex flex-col"
          :class="ui.drawer.right.isOpen && 'md:pr-2'"
        >
          <RouterView />
        </div>
        <!--
          DrawerHost + Notification (native driver) render INSIDE
          <main> so they're bounded to the main content region (under
          the header, right of the sidebar) — not the full viewport.
          DrawerHost auto-closes on route change via its own watcher.
        -->
        <DrawerHost />
        <Notification />
        <SyncActivityPane />
      </main>

      <!--
        Desktop sidebar-compact toggle — a slim chevron pill that
        straddles the sidebar/main divider at vertical center.
        Hidden by default; revealed only while the operator is
        hovering the sidebar (via Tailwind's `peer-hover:` — the
        sidebar carries the `peer` class above) OR hovering the pill
        itself (the `hover:opacity-100` rule keeps it visible during
        the cursor traverse from sidebar onto pill, otherwise the
        moment focus leaves the sidebar the pill would fade out
        before the click landed).

        Geometry:
          - parent `<div .flex .relative>` is the positioned ancestor
          - `top-1/2 -translate-y-1/2` centers vertically
          - `left-60` / `left-16` pins to sidebar's right edge across
            expanded / compact widths
          - `-translate-x-1/2` pulls the pill HALF over the divider
            so it visually sits ON the line, not next to it
          - `w-4` (16px) keeps it slim — just enough to host the
            tiny chevron with ~3-4px of breath either side
        Hidden on mobile (`hidden md:flex`); the hamburger handles
        the mobile toggle.
      -->
      <!--
        Per-property transition durations:
          - `opacity` runs on `duration-slow` (340ms) so the fade-in
            on sidebar-hover feels deliberate.
          - `left` / `color` / `background-color` run on
            `duration-normal` (220ms) — same token the sidebar uses
            for its width animation. This keeps the pill GLUED to
            the sidebar's right edge while the sidebar collapses /
            expands. Mixing them on a single `duration-slow` lagged
            the pill behind the divider during the toggle.
        Tailwind utilities can only express ONE duration per
        `transition-*` shorthand, so we drop down to a style
        binding here — referencing the same `--theme-duration-*`
        tokens Tailwind maps to.
      -->
      <button
        type="button"
        class="hidden md:flex absolute top-1/2 -translate-y-1/2 -translate-x-1/2 z-20 w-3 h-[4.5rem] items-center justify-center rounded-full bg-surface-elevated border border-border text-secondary hover:text-primary hover:bg-surface-hover opacity-0 peer-hover:opacity-100 hover:opacity-100 shadow-sm"
        :class="ui.sidebarCompact ? 'left-16' : 'left-60'"
        :style="{
          transition: [
            'opacity var(--theme-duration-slow) var(--theme-easing-decel)',
            'left var(--theme-duration-normal) var(--theme-easing-decel)',
            'color var(--theme-duration-normal) var(--theme-easing-decel)',
            'background-color var(--theme-duration-normal) var(--theme-easing-decel)',
          ].join(', '),
        }"
        :aria-label="ui.sidebarCompact ? 'Expand sidebar' : 'Collapse sidebar'"
        :title="ui.sidebarCompact ? 'Expand sidebar' : 'Collapse sidebar'"
        @click="ui.toggleSidebarCompact()"
      >
        <i
          class="pi text-[0.55rem]"
          :class="ui.sidebarCompact ? 'pi-chevron-right' : 'pi-chevron-left'"
        />
      </button>
    </div>
    <Footer />
  </div>
</template>
